import { combineSlices, configureStore, type Action } from '@reduxjs/toolkit';
import { isObject, mapValues, size } from 'lodash';
import { slicesList } from './slices';

// `combineSlices` automatically combines the reducers using
// their `reducerPath`s, therefore we no longer need to call `combineReducers`.
const rootReducer = combineSlices(...slicesList);
// Infer the `RootState` type from the root reducer
export type RootState = ReturnType<typeof rootReducer>;

/*
 * Large read-only API data (tens of thousands of partners, the full currency rates matrix).
 * The development-only checks and Redux DevTools would walk or serialize all of it on the actions below,
 * which blocks the main thread for hundreds of milliseconds. It is plain JSON written only by reducers,
 * so skipping it in the checks is safe. The checks run only in development; the DevTools sanitizers apply
 * wherever the Redux DevTools extension is connected.
 * Action types are plain strings (as in the lists below): importing the slices' action creators here creates
 * circular dependencies through `store/types`.
 */
const LARGE_DATA_PATHS = ['adminFullPartnersList', 'currencyRates', 'header.partners', 'header.partnerCurrencies'];

const getSizePlaceholder = (value: unknown): string => `<<${isObject(value) ? size(value) : 0} items>>`;

// the header partners payload is `{ partners, currencies }`, both lists are replaced
const getHeaderPartnersPlaceholder = (payload: unknown): unknown => {
    return isObject(payload) ? mapValues(payload, getSizePlaceholder) : payload;
};

const largeDataActionSanitizers: Partial<Record<string, (payload: unknown) => unknown>> = {
    'adminFullPartnersList/getAdminFullPartnersListSucceeded': getSizePlaceholder,
    'currencyRates/setCurrencyRates': getSizePlaceholder,
    'header/getHeaderPartnersSucceeded': getHeaderPartnersPlaceholder,
};

const stateSanitizer = <S>(state: S): S => {
    const { adminFullPartnersList, currencyRates, header } = state as unknown as RootState;

    return {
        ...state,
        adminFullPartnersList: { ...adminFullPartnersList, data: getSizePlaceholder(adminFullPartnersList.data) },
        currencyRates: { ...currencyRates, data: getSizePlaceholder(currencyRates.data) },
        header: {
            ...header,
            partners: getSizePlaceholder(header.partners),
            partnerCurrencies: getSizePlaceholder(header.partnerCurrencies),
        },
    };
};

const actionSanitizer = <A extends Action>(action: A): A => {
    const sanitizePayload = largeDataActionSanitizers[action.type];

    return sanitizePayload && 'payload' in action ? { ...action, payload: sanitizePayload(action.payload) } : action;
};

export const store = configureStore({
    reducer: rootReducer,
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            immutableCheck: {
                ignoredPaths: LARGE_DATA_PATHS,
            },
            serializableCheck: {
                // Ignore the path in the state where the regex lives, and the large API data
                ignoredPaths: [
                    'pageValidation',
                    'template',
                    'queryConfigurations',
                    'dynamicSegment',
                    ...LARGE_DATA_PATHS,
                ],
                // Ignore the action that carries the regex, and the actions that carry the large API data
                ignoredActions: [
                    'pageValidation/setPageValidation',
                    'pageValidation/changePageValidation',
                    'template/addNewTemplateList',
                    'template/setUIConfig',
                    'queryConfigurations/setBaseQueryConfigurations',
                    'queryConfigurations/setExclusionRulesQueryConfigurations',
                    'dynamicSegment/addNewDynamicSegmentFilter',
                    'dynamicSegment/setDynamicSegmentDefaultFilterList',
                    ...Object.keys(largeDataActionSanitizers),
                ],
            },
        }),
    // Redux DevTools shows the large API data as size placeholders instead of serializing it on every action
    devTools: { stateSanitizer, actionSanitizer },
});
